import { CookieSet, RoleSet, RoleTitleGroup } from "../../../types/campaignTypes";

export type RoleTitleCookieState = Record<string, CookieSet[]>;

export const getRoleKey = (roleTypeId: string, roleId: string) => `${roleTypeId}:${roleId}`;

/** Depth-first walk of a role tree. */
export const walkRoleSets = (roles: RoleSet[], visit: (role: RoleSet) => void) => {
  roles.forEach((role) => {
    visit(role);
    if (role.children?.length) walkRoleSets(role.children, visit);
  });
};

/** All cookieSets under a role including nested children. */
export const collectCookieSets = (role: RoleSet): CookieSet[] => {
  const result = [...(role.cookieSets || [])];
  (role.children || []).forEach((child) => {
    result.push(...collectCookieSets(child));
  });
  return result;
};

export const getSelectedAdLibIds = (cookieSets: RoleTitleCookieState): number[] => {
  const ids = new Set<number>();

  Object.entries(cookieSets).forEach(([key, values]) => {
    if (key.includes("_ALL")) return;
    values.forEach((cookie) => ids.add(cookie.adLibId));
  });

  return [...ids];
};

export const getCookieStateFromAdLibIds = (
  roleTitleList: RoleTitleGroup[],
  adLibIds: number[],
): RoleTitleCookieState => {
  const idSet = new Set(adLibIds.map((id) => Number(id)));

  return roleTitleList.reduce<RoleTitleCookieState>((acc, group) => {
    walkRoleSets(group.roleSet, (roleSet) => {
      const selectedCookies = roleSet.cookieSets.filter((cookie) => idSet.has(Number(cookie.adLibId)));
      if (selectedCookies.length > 0) {
        acc[getRoleKey(group.roleTypeId, roleSet.roleId)] = selectedCookies;
      }
    });
    return acc;
  }, {});
};

export const getRoleTitleLabelsFromAdLibIds = (roleTitleList: RoleTitleGroup[], adLibIds: number[]): string[] => {
  const labelsById = roleTitleList.reduce<Record<number, string>>((acc, group) => {
    walkRoleSets(group.roleSet, (roleSet) => {
      roleSet.cookieSets.forEach((cookie) => {
        acc[Number(cookie.adLibId)] = `${group.roleType} - ${roleSet.role}` || cookie.title;
      });
    });
    return acc;
  }, {});

  const names = adLibIds.map((id) => labelsById[Number(id)]).filter((label): label is string => Boolean(label));
  return [...new Set(names)];
};

export const getCheckboxStateFromCookieState = (
  roleTitleList: RoleTitleGroup[],
  cookieSets: RoleTitleCookieState,
): Record<string, boolean> => {
  const base = roleTitleList.reduce<Record<string, boolean>>((acc, group) => {
    walkRoleSets(group.roleSet, (roleSet) => {
      acc[getRoleKey(group.roleTypeId, roleSet.roleId)] = false;
    });
    return acc;
  }, {});

  Object.entries(cookieSets).forEach(([key, values]) => {
    base[key] = values.length > 0;
  });

  return base;
};

/** Apply select/deselect to a role and all of its descendants. */
export const applySubtreeSelection = (
  state: RoleTitleCookieState,
  roleTypeId: string,
  role: RoleSet,
  select: boolean,
): RoleTitleCookieState => {
  const next = { ...state };
  walkRoleSets([role], (rs) => {
    const key = getRoleKey(roleTypeId, rs.roleId);
    if (select) {
      next[key] = collectCookieSets(rs);
    } else {
      delete next[key];
    }
  });
  return next;
};

/** True when this role and every descendant are selected. */
export const isRoleSubtreeSelected = (
  roleTypeId: string,
  role: RoleSet,
  checkBoxes: Record<string, boolean>,
): boolean => {
  let all = true;
  let any = false;
  walkRoleSets([role], (rs) => {
    any = true;
    if (!checkBoxes[getRoleKey(roleTypeId, rs.roleId)]) all = false;
  });
  return any && all;
};
